/**
 * ComputedDataSource (abstract) — a `dataSource` whose cells are
 * produced by a deterministic function of the row's attributes +
 * date range. Peer of `FormulaDataSource` — both have
 * engine-managed cells, both reject user writes.
 *
 * Dispatched via `COMPUTED_SOURCE_BUILDERS` at hydrate time
 * (`computed-source-registry.ts`). Subclass hook:
 * `computeCellsForRange(range)` returns the cells for the
 * requested range; `recompute(range)` runs the hook and seeds
 * the sheet's `sourceCellData` store.
 *
 * Why not route through `dataOwner: CellProvider` like lona-js?
 * tento/tento-lona-js/sheets has no `dataOwner` concept on the descriptor
 * (cells are routed purely via role + canonical-key lookup).
 * Turning the wire-type → subclass dispatch into a registry
 * gives us the same per-type flexibility without bolting a new
 * descriptor field on.
 */

import type { Option } from "../../core/option.ts";
import { Json } from "../../core/json.ts";
import { sheetInternals, sheetOf, sheetStores } from "../../sheet/sheet-stores.ts";
import type { NaiveDate } from "@tento-chrono";
import type { JsonValue } from "@tento-core/json-reader";

import type { CellEntry } from "../../core/dtype.ts";
import { resolveRelativeLookupKey } from "../../ids/data-ref-resolve.ts";
import { declareEngineOwnedCells, SourceRow } from "./source-row.ts";
import { declareRowLoader, type RowLoadContext } from "../row-load.ts";

/**
 * The computed-row classes that register dependency edges, most specific
 * first: each declares itself once, from its static block
 * (`declareDependencyEdges`). Filled while the variant classes load and never
 * after. A registrar answers whether it handled the row.
 */
const DEPENDENCY_EDGES: Array<(row: ComputedDataSource) => boolean> = [];

/**
 * One computed-row class's recompute memo. `"signature"`
 * (`declareRecomputeMemo`): a pure overwrite keyed by the input-signature
 * string. `"inputs"` (`declareRecomputeMemoByInputs`): a rewrite that clears
 * the row's own cells in range first, keyed by identity tokens compared
 * element by element (`inputs`, read again after the rewrite); its
 * `dependencySignature` is built only for a ledger shadow's referee.
 */
type RecomputeMemo =
  & {
    readonly owns: (row: ComputedDataSource) => boolean;
    readonly dependencySignature: (row: ComputedDataSource, range: NaiveDate.Range) => string;
  }
  & (
    | { readonly kind: "signature" }
    | { readonly kind: "inputs"; readonly inputs: (row: ComputedDataSource, range: NaiveDate.Range) => readonly unknown[] }
  );

/**
 * The computed-row classes whose recompute may short-circuit through a memo
 * (`declareRecomputeMemo`, `declareRecomputeMemoByInputs`), most specific
 * first: the one memo mechanism of every computed row. Filled while the
 * variant classes load and never after. A class that declares none is never
 * memoized: opt-in keeps any future subclass correct by default.
 */
const RECOMPUTE_MEMOS: Array<RecomputeMemo> = [];

/**
 * Declare that `cls`'s recompute is a pure overwrite, so it may short-circuit
 * through the memo, and how to digest its EXTERNAL inputs for the memo key:
 * `dependencySignature(row, range)` names every cell it reads from OTHER rows
 * (identity + cells in range), or `""` for a row that reads none (a pure
 * function of the date range and its attributes). Called once per class from
 * its static block — the package's replacement for the overridable
 * `memoizableRecompute` / `dependencySignature`; not on a barrel.
 */
export function declareRecomputeMemo<T extends ComputedDataSource>(
  cls: abstract new (...args: never[]) => T,
  dependencySignature: (row: T, range: NaiveDate.Range) => string,
): void {
  RECOMPUTE_MEMOS.unshift({
    kind: "signature",
    owns: (row) => row instanceof cls,
    dependencySignature: (row, range) => row instanceof cls ? dependencySignature(row, range) : "",
  });
}

/**
 * Declare that `cls`'s recompute clears its own cells in range and rewrites
 * them, and may short-circuit when `inputs(row, range)` — identity tokens (the
 * range, the frozen attribute bag, each input row's id and `cellsRevision()`,
 * the row's own `cellsRevision()`), compared element by element and read
 * again after each rewrite — is unchanged; no cell is read for the key.
 * `dependencySignature` digests the external cell inputs as
 * `declareRecomputeMemo`'s does, and is built only when a ledger shadow
 * referees the row. Called once per class from its static block; not on a
 * barrel.
 */
export function declareRecomputeMemoByInputs<T extends ComputedDataSource>(
  cls: abstract new (...args: never[]) => T,
  inputs: (row: T, range: NaiveDate.Range) => readonly unknown[],
  dependencySignature: (row: T, range: NaiveDate.Range) => string,
): void {
  RECOMPUTE_MEMOS.unshift({
    kind: "inputs",
    owns: (row) => row instanceof cls,
    inputs: (row, range) => row instanceof cls ? inputs(row, range) : [],
    dependencySignature: (row, range) => row instanceof cls ? dependencySignature(row, range) : "",
  });
}

function sameInputs(a: readonly unknown[], b: readonly unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Declare how `cls`'s rows register their dependency edges (source → row)
 * into the sheet's graph, so a source's cell change recomputes them. Called
 * once per class from its static block — the package's replacement for an
 * overridable `registerDependencyEdges`; not on a barrel.
 */
export function declareDependencyEdges<T extends ComputedDataSource>(
  cls: abstract new (...args: never[]) => T,
  register: (row: T) => void,
): void {
  DEPENDENCY_EDGES.unshift((row) => {
    if (!(row instanceof cls)) return false;
    register(row);
    return true;
  });
}

/**
 * The base of a source row whose cells are computed from its attributes and the date range, and
 * which refuses user writes.
 */
export abstract class ComputedDataSource extends SourceRow {
  static {
    declareEngineOwnedCells(ComputedDataSource, (row, method) => {
      const name = row.label ?? row.id.rawId;
      const verb = method === "write" ? "writes are" : "clears are";
      return `ComputedDataSource.${method}: row "${name}" is computed — ` +
        `${verb} rejected because the row's cells are engine-owned.`;
    });
    declareRowLoader(ComputedDataSource, {
      value: (row, cx) => row.#loadValue(cx),
      children: null,
    });
  }

  /**
   * The row's declared **relative** address, or `null` for an absolute
   * row. A generic computed row declares a `localPath` in its attribute bag;
   * its concrete key resolves to `nearestKeyedAncestor.lookupKey + "." +
   * localPath` (see the `lookupKey` override). Design:
   * lona-sdk-2.0.project.docs/content/data-address/adoption.md (Decisions OQ1).
   */
  get localPath(): Option<string> {
    return Json.string(this.attributes(), "localPath");
  }

  /**
   * A computed row is **addressable by its resolved key**: its own absolute
   * `lookupKey` (when the row declares one), else the relative `localPath`
   * resolved against the nearest keyed ancestor (`parent.lookupKey + "." +
   * localPath`). Overriding the identity getter — rather than storing the
   * resolved key — keeps the row **portable** (reparenting re-derives it for
   * free) and lets chains compose: an ancestor computed row's resolved key
   * feeds its descendants, so `:~garmin:sleep` → `.deep` → `.deep.smoothed`.
   * Unset when neither an absolute key nor a resolvable `localPath`
   * exists (not yet addressable). Read-only by nature (the formula derives the
   * cells). Design: data-address/adoption.md (Decisions OQ1 / OQ5).
   */
  override get lookupKey(): Option<string> {
    const stored = super.lookupKey;
    if (stored != null) return stored;
    const lp = this.localPath;
    if (lp != null) return resolveRelativeLookupKey(this, lp);
    return null;
  }

  /**
   * Load a computed row's value (its `declareRowLoader` entry): it owns no
   * network-backed cells of its own, so it triggers no backend fetch on
   * `this`. It registers its dependency edges, then loads its descendants
   * (constituent SourceRows of a `ListDataSource` aggregate, formula
   * upstream sources, etc.), each concrete `SourceRow` fetching its own.
   */
  async #loadValue(cx: RowLoadContext): Promise<void> {
    this.#registerDependencyEdges();
    // L3 (dark): mirror this row's freshly-registered edges onto the ledger.
    // Reached via the store field to avoid value-importing `ledger-shadow`
    // (TDZ through `source-row`; see that module's header).
    sheetStores(sheetOf(this)).sourceCellData.ledgerShadow?.declareFromGraph(
      sheetInternals(sheetOf(this)).graph,
      this,
    );
    await Promise.all(this.children.map((child) => cx.tree(child)));
  }

  /**
   * Register this row's dependency edges (source → this) into `sheet.graph` so
   * the sheet's `ColumnDataChanged` handler recomputes it when a source's cells
   * change (`enqueueDependentRecomputes`). No-op by default; `DerivedDataSource`
   * overrides it. Called from `ensureLoaded` (after hydrate, before the source's
   * async fetch resolves) AND `recompute` (self-heal — `buildDependencyGraph`
   * wipes the whole graph on formula changes, so re-register on each recompute).
   * Idempotent: `addDependencyEdge` dedupes. A subclass whose recompute
   * reads other rows declares its edges once, from its static block
   * (`declareDependencyEdges`).
   */
  #registerDependencyEdges(): void {
    for (const registrar of DEPENDENCY_EDGES) if (registrar(this)) return;
  }
  /**
   * Compute the cell entries this row owns for the given date
   * range. Called from `recompute`; the subclass is responsible
   * for reading any runtime inputs (e.g. `this.attributes()`,
   * `this.children`) and producing the cell list.
   *
   * Returning `[]` is valid (no cells this range). Cells returned
   * outside the requested range are ignored — `recompute` clips
   * to the range before writing.
   */
  abstract computeCellsForRange(range: NaiveDate.Range): CellEntry[];

  /**
   * Memo of the last recompute we actually wrote cells for: the
   * `(range + config + dependency-input)` signature at that point. The caller
   * (`Sheet.recomputeComputedSources`) recomputes EVERY computed row on every
   * sheet open / switch, but the vast majority of those calls are redundant —
   * nothing this row reads has changed since the last one (gap G5). `null`
   * until the first write. Shares the row instance's lifetime with its cells:
   * the only path that clears a computed row's own cells (`Sheet.deleteRow`)
   * also drops the row from `rowsById`, so a stale memo can never outlive the
   * cells it describes.
   */
  #memoSignature: Option<string> = null;

  /**
   * Engine write — seeds `sourceCellData` with the subclass's computed cells
   * for `range`. Uses the store's write path so `ColumnDataChanged`
   * subscribers see the refresh; no network round-trip. Safe to call
   * repeatedly — later invocations overwrite earlier ones at the same date.
   *
   * Memoized for the classes that opt in with `declareRecomputeMemo` (pure
   * overwrite, no destructive pre-clear): short-circuits when the freshly
   * computed input signature matches the one we last wrote for (G5). The
   * dependency graph is re-registered on EVERY call regardless — it self-heals
   * `buildDependencyGraph`'s whole-graph wipe and is cheap + idempotent,
   * unlike the compute + per-cell `set` fan-out the memo actually skips.
   */
  recompute(range: NaiveDate.Range): ComputedDataSource.RecomputeOutcome {
    this.#registerDependencyEdges();
    // L3 (dark): mirror this row's edges onto the ledger, then let the LI2
    // referee observe the D4 signature vs the ledger's freshness verdict.
    // Reached via the store field to avoid value-importing `ledger-shadow`
    // (TDZ through `source-row`; see that module's header).
    const shadow = sheetStores(sheetOf(this)).sourceCellData.ledgerShadow;
    shadow?.declareFromGraph(sheetInternals(sheetOf(this)).graph, this);
    const memo = RECOMPUTE_MEMOS.find((m) => m.owns(this));
    if (memo?.kind === "inputs") return this.#recomputeByInputs(memo, range, shadow);
    if (memo != null) {
      const signature = this.#recomputeSignature(range, memo.dependencySignature(this, range));
      if (this.#memoSignature !== null && this.#memoSignature === signature) {
        shadow?.checkAndRecord(this, signature);
        return "hit";
      }
      this.#writeComputedCells(range);
      this.#memoSignature = signature;
      shadow?.checkAndRecord(this, signature);
      return "miss";
    }
    this.#writeComputedCells(range);
    return "unmemoized";
  }

  /** The `"inputs"` memo's tokens at the last rewrite, or empty before the first. */
  #memoInputs: readonly unknown[] = [];

  /**
   * `recompute` under an `"inputs"` memo: same tokens, the rewrite would store
   * what is already stored (`"hit"`); else clear the row's cells in `range` and
   * rewrite them in one deferred-listener window — the clear+rewrite emits 2N
   * `ColumnDataChanged`, drained once — and take the tokens again (the row's
   * own revision moved). The referee's signature is built only for a shadow.
   */
  #recomputeByInputs(
    memo: RecomputeMemo & { readonly kind: "inputs" },
    range: NaiveDate.Range,
    shadow: Option<{ checkAndRecord(consumer: ComputedDataSource, signature: string): void }>,
  ): ComputedDataSource.RecomputeOutcome {
    const hit = sameInputs(memo.inputs(this, range), this.#memoInputs);
    if (!hit) {
      this.#writeComputedCells(range, true);
      this.#memoInputs = memo.inputs(this, range);
    }
    shadow?.checkAndRecord(this, this.#recomputeSignature(range, memo.dependencySignature(this, range)));
    return hit ? "hit" : "miss";
  }

  /**
   * Compute this row's cells and seed them, coalescing the per-cell
   * `ColumnDataChanged` cascade. Without `withDeferredListeners` each `set`
   * re-enters the sheet's listener (parent-recompute drain + alias
   * re-expansion) — an O(cells) storm per recompute; the wrap collapses that
   * into a single drain when the loop completes (re-entrant, so a caller
   * already deferring is unaffected). `clearFirst` (an `"inputs"` memo's
   * rewrite) first clears the row's cells in `range`, in the same deferred
   * window and outside the transaction, then computes and writes.
   */
  #writeComputedCells(range: NaiveDate.Range, clearFirst = false): void {
    const internals = sheetInternals(sheetOf(this));
    const cells = sheetStores(sheetOf(this)).sourceCellData;
    const writeAll = (entries: readonly CellEntry[]): void => {
      const write = (): void => {
        internals.withDeferredListeners(() => {
          for (const entry of entries) cells.set(this.id, entry.date, entry.value, entry.id ?? null, null, null);
        });
      };
      // TX0: group a recompute's cell writes into ONE explicit
      // `computed-recompute` transaction. Reached via the bumper — variants MUST
      // NOT value-import `tx-log` (TDZ); `txScope` runs `write` directly when no
      // recorder is attached.
      const bumper = cells.ledgerBumper;
      if (bumper != null) bumper.txScope("computed-recompute", write);
      else write();
    };
    if (!clearFirst) {
      writeAll(this.computeCellsForRange(range));
      return;
    }
    // The clear+rewrite emits 2N `ColumnDataChanged`; one deferred window drains them once.
    internals.withDeferredListeners(() => {
      for (const entry of cells.cells(this.id, range)) cells.clear(this.id, entry.date, entry.id ?? null, null);
      writeAll(this.computeCellsForRange(range));
    });
  }

  /**
   * The memo key: everything `computeCellsForRange` can read, cheaply
   * digested. Correctness rests on this being a SUPERSET of the compute's
   * inputs — any un-captured input would let a needed recompute be skipped:
   *
   *   - `range` — the requested window (a widen / shrink / shift changes it);
   *   - the row's attribute bag — every config knob a subclass reads (`factor`
   *     / `field` / `weights` / `blend` / `source` / `calendarSystem` /
   *     `localPath`), so a formula/config edit invalidates;
   *   - `dependencies` — the declared `dependencySignature(row, range)`
   *     (`declareRecomputeMemo`): the class's EXTERNAL cell inputs (the
   *     rows it projects FROM, in resolution order, with their cell values in
   *     range). Empty for rows that read no other row (`CalendarSystemRow` is a
   *     pure function of the date + system).
   *
   * Fully JSON so a delimiter collision can't fold two distinct input states
   * onto one key. Over-capture is safe (a spurious miss recomputes an identical
   * result); under-capture is not — hence "when in doubt, include."
   */
  #recomputeSignature(range: NaiveDate.Range, dependencies: string): string {
    return JSON.stringify({
      r: [range.start.dse, range.end.dse],
      a: this.attributes(),
      d: dependencies,
    });
  }

  /**
   * Engine owns the cells — never user-writable, so the UI renders them
   * read-only up front (e.g. `ColumnNumberRow.editable` consults this) instead
   * of presenting an editable cell whose write throws after the fact.
   * Matches `FormulaDataSource`; `assertWritable` below is the hard backstop
   * for any write that still reaches the data layer.
   */
  override get cellsWritable(): boolean {
    return false;
  }
}

/** Types that go with `ComputedDataSource`: `RecomputeOutcome`. */
export namespace ComputedDataSource {
  /**
   * What one `recompute` did: the memo served it (`hit`), it recomputed and
   * rewrote cells (`miss`), or the row does not memoize (`unmemoized`). `Sheet`
   * tallies these onto its `sheet/recompute-*` spans.
   */
  export type RecomputeOutcome = "hit" | "miss" | "unmemoized";
}
